// Tema de estilo del export Excel de Reportería — colores/fuentes medidos de la plantilla real del
// cliente (auditoría 2026-08-24, `02 - Campana brillamos juntos.xlsx`). No inventar valores: si un
// color no está en esta lista, se pidió referencia antes de escribir código.
import ExcelJS from 'exceljs';
import { luminancia, mezclarConBlanco } from './colorMix';

export const EXCEL_FONT_FAMILY = 'Aptos';

export const excelColors = {
  tituloFondo: 'FF173F4F',
  tituloTexto: 'FFFFFFFF',
  subtituloFondo: 'FFF7FBFC',
  subtituloTexto: 'FF5F6B73',
  metaFondo: 'FFEAF3F6',
  metaEtiqueta: 'FF342F82',
  metaValor: 'FF333333',
  headerTablaFondo: 'FF007A7A',
  headerTablaTexto: 'FFFFFFFF',
  filaPar: 'FFFFFFFF',
  filaImpar: 'FFF7FBFC',
  grupoFondo: 'FFE7F7F5',
  valorDestacado: 'FF173F4F',
  textoDetalle: 'FF5F6B73',
  notaFondo: 'FFFFF4E8',
  notaTexto: 'FF5F6B73',
  /** Texto de las filas de datos de las tablas — medido de la plantilla, no es negro puro. */
  textoCuerpo: 'FF24343B',
  /** Rejilla de bordes finos de cada celda de tabla, medida de la plantilla. */
  bordeTabla: 'FFD7E3E6',
  // Bug de producción 2026-08-26 (hoja "Embudo Adopción") — no es un color de marca inventado: es el
  // preset ESTÁNDAR "Green Data Bar" de la galería nativa de Formato Condicional de Excel
  // (Microsoft.Office.Interop.Excel `DataBarColor` RGB 99,195,132), el mismo que la plantilla de
  // referencia del cliente usa en esa columna.
  dataBarVerde: 'FF63C384',
} as const;

/**
 * Paleta PROPIA del Excel de Líderes, medida celda a celda de `06 - Lideres.xlsx` (auditoría
 * 2026-09-04). No coincide con `excelColors` — que se midió de `02 - Campana brillamos juntos.xlsx`
 * y es teal — porque esa plantilla del cliente es azul/amarilla: mezclarlas daría un archivo con
 * dos identidades. Mismo criterio ya aplicado en la Reportería de Moderación, que también trae su
 * propia paleta.
 */
export const excelColoresLideres = {
  tituloFondo: 'FF003A70',
  tituloTexto: 'FFFFFFFF',
  subtituloFondo: 'FFEAF3FF',
  subtituloTexto: 'FF666666',
  /** Banda amarilla que separa el banner del cuerpo de cada hoja. */
  bandaFondo: 'FFFFD100',
  /** Bloque "Periodo/Filtros/Generado": etiqueta en amarillo sólido, valor en amarillo claro. */
  metaEtiquetaFondo: 'FFFFD100',
  metaValorFondo: 'FFFFF4BF',
  headerTablaFondo: 'FF005EB8',
  headerTablaTexto: 'FFFFFFFF',
  /** Encabezado de la columna «Participación», destacada en amarillo en la plantilla. */
  headerDestacadoFondo: 'FFFFD100',
  headerDestacadoTexto: 'FF1F2933',
  /** Columnas de texto de las filas de datos (Líder/Puesto/OU/Nivel, y la columna «Dato»). */
  celdaTextoFondo: 'FFEAF3FF',
  /** Columnas numéricas de las filas de datos. */
  celdaNumeroFondo: 'FFFFFFFF',
  /** Columna «Participación» de las filas de datos. */
  celdaDestacadaFondo: 'FFFFF4BF',
  textoCuerpo: 'FF1F2933',
  /** Números del Resumen Ejecutivo: azul oscuro en negrita. */
  valorDestacado: 'FF003A70',
  bordeTabla: 'FFD8DEE9',
} as const;

/**
 * Paleta PROPIA del Excel Demográfico, medida celda a celda de `04 - Demografico.xlsx` (referencia
 * del cliente, auditoría 2026-09-04). No coincide con `excelColors` — que se midió de
 * `02 - Campana brillamos juntos.xlsx` y es teal — porque esta plantilla es azul marino / crema:
 * mezclarlas daría un archivo con dos identidades. Mismo criterio ya aplicado en Líderes y en la
 * Reportería de Moderación, que también traen su propia paleta.
 *
 * 🔴 Los colores de Fundamental (tintes de "Brecha Generacional" y "Heatmap OU × Fundamental") NO
 * salen de aquí: los sigue dando el catálogo vía `excelTintStyle`, igual que en pantalla.
 */
export const excelColoresDemografico = {
  tituloFondo: 'FF003A70',
  tituloTexto: 'FFFFFFFF',
  subtituloFondo: 'FFDDEBF7',
  subtituloTexto: 'FF003A70',
  /** Bloque "Periodo/Filtros/Generado": etiqueta en navy sólido, valor en crema. */
  metaEtiquetaFondo: 'FF003A70',
  metaEtiquetaTexto: 'FFFFFFFF',
  metaValorFondo: 'FFFFF2CC',
  metaValorTexto: 'FF003A70',
  headerTablaFondo: 'FFDDEBF7',
  headerTablaTexto: 'FF003A70',
  filaPar: 'FFFFFFFF',
  filaImpar: 'FFEEF5FB',
  textoCuerpo: 'FF1F1F1F',
  /** Primera columna de cada tabla (Generación / OU / Flujo): navy en negrita. */
  etiquetaFila: 'FF003A70',
  /** Columna «% del total» del Resumen Ejecutivo: azul en negrita. */
  porcentajeTexto: 'FF0070C0',
  /** Fila y columna «Total» del Heatmap: navy sólido con texto blanco. */
  totalFondo: 'FF003A70',
  totalTexto: 'FFFFFFFF',
  /** Columna «Total» por fila: crema azulado, menos peso que la fila Total general. */
  totalFilaFondo: 'FFEEF5FB',
  /** Columna «Nota» del Resumen Ejecutivo: la plantilla la marca en rojo. */
  notaColumnaTexto: 'FFFF0000',
  /** Nota al pie de cada hoja. */
  notaFondo: 'FFFFF2CC',
  notaTexto: 'FF003A70',
  bordeTabla: 'FFB7C9E2',
  bordeNota: 'FFE5C75A',
} as const;

/**
 * Paleta PROPIA del Excel de Históricos, medida celda a celda de `03 - Historicos.xlsx` (referencia
 * del cliente, auditoría 2026-09-05). No coincide con `excelColors` — que se midió de
 * `02 - Campana brillamos juntos.xlsx` y es teal — porque esta plantilla es azul acero: mezclarlas
 * daría un archivo con dos identidades. Mismo criterio ya aplicado en Líderes, Demográfico y
 * Moderación, que también traen su propia paleta.
 */
export const excelColoresHistoricos = {
  tituloFondo: 'FF1F4E78',
  tituloTexto: 'FFFFFFFF',
  subtituloFondo: 'FFF7FBFC',
  subtituloTexto: 'FF5F6B73',
  /** Bloque "Filtro aplicado / Generado": etiqueta y valor comparten el celeste de la plantilla. */
  metaFondo: 'FFEAF3F6',
  metaEtiqueta: 'FF342F82',
  metaValor: 'FF333333',
  headerTablaFondo: 'FF5B9BD5',
  headerTablaTexto: 'FFFFFFFF',
  filaPar: 'FFFFFFFF',
  filaImpar: 'FFF7FBFC',
  textoCuerpo: 'FF24343B',
  /** Columna «Valor» del Resumen Ejecutivo: azul petróleo en negrita. */
  valorDestacado: 'FF173F4F',
  bordeTabla: 'FFD9E6EA',
  /** Rejilla del encabezado de tabla, más oscura que la del cuerpo en la plantilla. */
  bordeHeader: 'FF007A7A',
} as const;

export function fillSolido(colorArgb: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb: colorArgb } };
}

/**
 * Bug de producción 2026-08-26 (gráfica de "Distribución Fundamental") — los colores de este tema
 * están en formato ARGB de Excel (`AARRGGBB`, alfa PRIMERO), pero un canvas/CSS espera `#RRGGBB`
 * (sin alfa primero). Pasarle un ARGB crudo a Chart.js no lanza error: el navegador simplemente no
 * reconoce el string como color válido y el `fillStyle` cae al negro por omisión — la barra "teal"
 * salía sólida NEGRA. Recorta los dos primeros dígitos (alfa) y antepone `#`.
 */
export function argbACss(colorArgb: string): string {
  return `#${colorArgb.slice(2)}`;
}

function componenteHex(valor: number): string {
  return valor.toString(16).padStart(2, '0').toUpperCase();
}

/**
 * Versión Excel (ARGB) de `fundamentalTintStyle` (`reportsShared.tsx`) — misma mezcla con blanco por
 * intensidad y el mismo umbral de luminancia para elegir texto blanco/oscuro; solo cambia el formato
 * de salida (ARGB de Excel en vez de `rgb()`/`#RRGGBB` de CSS), para que `cell.fill`/`cell.font` de
 * ExcelJS lo consuman directo.
 */
export function excelTintStyle(
  colorBase: string, ratio: number, piso: number,
): { fillArgb: string; fontArgb: string } {
  const intensidad = Math.min(1, Math.max(piso, ratio));
  const [rojo, verde, azul] = mezclarConBlanco(colorBase, intensidad);
  const fillArgb = `FF${componenteHex(rojo)}${componenteHex(verde)}${componenteHex(azul)}`;
  const fontArgb = luminancia([rojo, verde, azul]) < 0.55 ? 'FFFFFFFF' : 'FF1F2937';
  return { fillArgb, fontArgb };
}
